import { apiErrorMessage } from './prescriptionForm';
import { isEmailish } from './authForm';

/**
 * Pure logic for the forgot-password screen (ForgotPassword.js).
 *
 * Named forgotForm rather than forgotPassword: this repository pairs a screen
 * with a pure module under a DIFFERENT name, not a differently-cased one
 * (PhaseGridPage.js + phaseGrid.js). On a case-insensitive filesystem —
 * macOS, which is what this is developed on — ForgotPassword.js and
 * forgotPassword.js are one file, and the second one written silently replaces
 * the first.
 */

/**
 * The one thing this screen ever says after a successful request.
 *
 * Deliberately does NOT name the address or claim mail is on its way to it.
 * The API answers identically whether or not an account exists (api-contract.md
 * AUTH-12), and a client that said "we've emailed you@example.com" would hand
 * back the membership oracle the server just refused to be.
 */
export const FORGOT_CONFIRMATION =
  'If that email has an account, a reset link is on its way. It expires in an hour.';

const asText = (value) => String(value ?? '').trim();

/**
 * Validate the forgot-password form, returning field-keyed errors.
 *
 * Mirrors validateRegistrationForm's shape so the two screens report problems
 * the same way, and reuses isEmailish rather than restating the rule — a second
 * definition of "looks like an email" is a second thing to keep in step.
 *
 * @param {object} form
 * @returns {Object<string, string>} empty when the form is valid
 */
export function validateForgotForm(form = {}) {
  const errors = {};

  const email = asText(form.email);
  if (email === '') {
    errors.email = 'Enter your email address.';
  } else if (!isEmailish(email)) {
    errors.email = 'That does not look like an email address.';
  }

  return errors;
}

/**
 * Map a failed reset request to something the user can act on.
 *
 * Note what is missing: there is no branch for "no such account". The server
 * cannot tell us, on purpose, so any message implying it would be invented.
 */
export function forgotErrorMessage(error) {
  if (!error?.response) {
    return 'Could not reach the server. Check your connection and try again.';
  }
  // 404 means the endpoint is not mounted, which happens when the deployment
  // has no mail transport configured. Saying "try again" would be a lie — no
  // amount of retrying configures SMTP — so this points at the one action that
  // actually helps.
  if (error.response.status === 404) {
    return 'Password reset is not available on this deployment. Ask your coach to reset it for you.';
  }
  if (error.response.status === 400) {
    return 'Enter a valid email address.';
  }
  return apiErrorMessage(error, 'Something went wrong sending your reset link.');
}
